import { AccessRuleEntitlementContext } from "../models";
import { LiftAccessRuleSaveResult } from "../../models";
import { entitlementContextKey, toEntitlementContext } from "./models";

/** Shared result handling for the bulk runners (add / save / delete). */

/** Shown in the outcome grid for a rule set the API said nothing about. */
export const NO_RESULT_MESSAGE = "No result returned for this rule set";

/** The API answers camelCase; our own code PascalCase. */
const toSaveResult = (raw: any): LiftAccessRuleSaveResult => ({
  success: !!(raw?.success ?? raw?.Success),
  context: toEntitlementContext(raw?.context ?? raw?.Context),
  errorCode: raw?.errorCode ?? raw?.ErrorCode,
  operation: raw?.operation ?? raw?.Operation,
  changeSetCategory: raw?.changeSetCategory ?? raw?.ChangeSetCategory,
});

/**
 * One result per context sent, no more and no less — an unaccounted rule set on an
 * irreversible operation is the worst outcome. Unreadable contexts are dropped and
 * re-reported as unanswered, so each is still counted exactly once.
 */
export const reconcileResults = (
  sent: AccessRuleEntitlementContext[],
  rawResults: unknown[] | undefined,
): LiftAccessRuleSaveResult[] => {
  const attributed: LiftAccessRuleSaveResult[] = [];

  for (const raw of rawResults ?? []) {
    const result = toSaveResult(raw);
    if (!result.context) {
      console.warn("Bulk run: result with an unreadable context", raw);
      continue;
    }
    attributed.push(result);
  }

  const answered = new Set(
    attributed.map((r) => entitlementContextKey(r.context!)),
  );

  const unanswered = sent
    .filter((context) => !answered.has(entitlementContextKey(context)))
    .map((context) => ({
      success: false,
      context,
      errorMessage: NO_RESULT_MESSAGE,
    }));

  return attributed.concat(unanswered);
};

/**
 * Counts derived from the results themselves. Taking them from the API's own
 * `successCount` instead lets the headline number and the grid below it disagree.
 */
export const countOutcomes = (results: LiftAccessRuleSaveResult[]) => {
  const successCount = results.filter((r) => r.success).length;
  return {
    successCount,
    failureCount: results.length - successCount,
  };
};

export const firstString = (...values: any[]): string | undefined => {
  for (const v of values) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
};

export type ApiErrorDetail = {
  errorStatus?: number;
  errorCode?: string;
  errorMessage?: string;
};

/** Failure detail from a dispatched RSAA result. */
export const extractApiError = (dispatched: any): ApiErrorDetail => {
  const payload = dispatched?.payload;
  if (!payload) return {};

  const isHttpError = !!dispatched?.error;
  // On an HTTP error the body is in payload.response; on a 2xx success:false it IS
  // the payload.
  const body = isHttpError ? payload.response : payload;

  // Structured save-changes error: { operation, changeSetCategory, errorCode }.
  const apiError = body && typeof body === "object" ? body.error : undefined;

  const bodyDetail =
    typeof body === "string"
      ? body
      : firstString(
          apiError?.message,
          body?.detail,
          body?.message,
          body?.title,
          Array.isArray(body?.errors) ? body.errors[0] : undefined,
        );

  return {
    errorStatus:
      isHttpError && typeof payload.status === "number" ? payload.status : undefined,
    errorCode: firstString(apiError?.errorCode),
    // NOT payload.message — already "{status} - {statusText}", so it would duplicate
    // the status shown alongside it.
    errorMessage: firstString(bodyDetail, isHttpError ? payload.statusText : undefined),
  };
};

/** Per-context results out of a set-based response. Read on 400 too: the API answers
 *  `BadRequest(result)` when nothing succeeded. */
export const extractResultsArray = (dispatched: any): unknown[] | undefined => {
  const payload = dispatched?.payload;
  if (!payload) return undefined;

  const body = dispatched?.error ? payload.response : payload;
  const results = body?.results ?? body?.Results;

  return Array.isArray(results) ? results : undefined;
};
